import React, { useState, useRef } from 'react';
import { storage } from '../firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { collection, addDoc, doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { syncArtworkVisibility } from '../utils/artworkVisibilitySync';
import { trackEvent, logError } from '../utils/observability';
import { optimizeImageForUpload, createThumbnailForUpload } from '../utils/imageUploadUtils';

const ArtworkUploadForm = ({ activityId, onUploadSuccess, onUploadComplete }) => {
  const { user } = useAuth();
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [showHostModal, setShowHostModal] = useState(false);
  const [submissionId, setSubmissionId] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file size (1MB limit)
    if (file.size > 1024 * 1024) {
      setError('File size must be less than 1MB');
      return;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    setError('');
    setSelectedFile(file);

    // Create preview URL
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreviewUrl(e.target.result);
    };
    reader.readAsDataURL(file);
  };

  const handleUpload = async () => {
    if (!selectedFile || !user) return;

    setUploading(true);
    setError('');

    try {
      // Generate unique submission ID
      const submissionId = `submission_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      // Upload to Firebase Storage under user-scoped path
      const storageRef = ref(storage, `users/${user.uid}/artworks/${submissionId}.jpg`);
      const optimizedFile = await optimizeImageForUpload(selectedFile);
      const thumbnailFile = await createThumbnailForUpload(optimizedFile || selectedFile);
      const uploadResult = await uploadBytes(storageRef, optimizedFile || selectedFile);
      const downloadURL = await getDownloadURL(uploadResult.ref);
      let thumbnailUrl = downloadURL;
      if (thumbnailFile) {
        const thumbRef = ref(storage, `users/${user.uid}/artworks/thumbnails/${submissionId}.jpg`);
        await uploadBytes(thumbRef, thumbnailFile);
        thumbnailUrl = await getDownloadURL(thumbRef);
      }

      // Create submission document
      const submissionData = {
        activityId,
        uid: user.uid,
        imageUrl: downloadURL,
        thumbnailUrl,
        // Denormalized artist display fields for co-learning UIs
        artistName: user.displayName || user.email || '',
        artistPhotoURL: user.photoURL || '',
        artistType: user.accountType || 'student',
        isPublic: false, // Initially private
        likesCount: 0,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const submissionRef = await addDoc(collection(db, 'submissions'), submissionData);
      trackEvent('artwork_uploaded', {
        submissionId: submissionRef.id,
        activityId,
        userId: user.uid,
        location: 'ArtworkUploadForm',
      });
      setSubmissionId(submissionRef.id);

      // Create activity participant record
      const participantId = `${activityId}_${user.uid}`;
      await setDoc(doc(db, 'activityParticipants', participantId), {
        activityId,
        uid: user.uid,
        completed: true,
        createdAt: serverTimestamp()
      });

      // Call success callback
      if (onUploadSuccess) {
        onUploadSuccess(submissionRef.id, downloadURL);
      }

      // Show host modal
      setShowHostModal(true);

    } catch (error) {
      console.error('Upload error:', error);
      logError(error, { where: 'ArtworkUploadForm.handleSubmit', activityId, userId: user?.uid || null });
      setError('Failed to upload artwork. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleHostDecision = async (shouldHost) => {
    if (!submissionId) return;

    try {
      // Get current submission to check old visibility
      const submissionDoc = await getDoc(doc(db, 'submissions', submissionId));
      const oldIsPublic = submissionDoc.exists() ? submissionDoc.data().isPublic : false;
      const submissionData = submissionDoc.exists() ? { id: submissionId, ...submissionDoc.data() } : null;

      // Update submission to public if user chooses to host
      if (shouldHost) {
        await doc(db, 'submissions', submissionId).update({
          isPublic: true,
          updatedAt: serverTimestamp()
        });

        // Sync with co-learning space if artwork is from an activity
        if (submissionData) {
          await syncArtworkVisibility(
            { ...submissionData, isPublic: true },
            true,
            oldIsPublic,
            'submissions'
          );
        }
      }

      // Call completion callback
      if (onUploadComplete) {
        onUploadComplete(shouldHost);
      }

      setShowHostModal(false);
      
      // Reset form
      setSelectedFile(null);
      setPreviewUrl(null);
      setSubmissionId(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

    } catch (error) {
      console.error('Error updating submission:', error);
      setError('Failed to update submission. Please try again.');
    }
  };

  return (
    <div className="bg-gradient-to-br from-amber-50/90 to-orange-50/90 backdrop-blur-sm border border-amber-200/60 rounded-2xl p-6 shadow-lg">
      <h2 className="text-2xl font-bold text-amber-800 mb-4 text-center">
        Upload Your Artwork
      </h2>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4">
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {!selectedFile ? (
        <div className="space-y-4">
          <div className="border-2 border-dashed border-amber-300 rounded-xl p-8 text-center hover:border-amber-400 transition-colors">
            <div className="text-amber-600 mb-4">
              <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
            </div>
            <p className="text-amber-700 mb-2">Click a picture of your artwork and upload</p>
            <p className="text-sm text-amber-600 mb-4">Maximum file size: 1MB</p>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              onChange={handleFileSelect}
              className="hidden"
              id="artwork-upload"
            />
            <label
              htmlFor="artwork-upload"
              className="bg-amber-500 hover:bg-amber-600 text-white font-semibold py-2 px-6 rounded-full cursor-pointer transition-colors"
            >
              Take or upload photo
            </label>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Preview */}
          <div className="text-center">
            <img
              src={previewUrl}
              alt="Artwork preview"
              className="max-w-full max-h-64 mx-auto rounded-xl shadow-md"
            />
          </div>

          {/* Upload Button */}
          <div className="text-center">
            <button
              onClick={handleUpload}
              disabled={uploading}
              className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 disabled:from-amber-300 disabled:to-orange-300 text-white font-bold py-3 px-8 rounded-full transition-all duration-300 shadow-lg hover:shadow-xl disabled:cursor-not-allowed"
            >
              {uploading ? (
                <div className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
                  </svg>
                  Uploading...
                </div>
              ) : (
                'Upload Artwork'
              )}
            </button>
          </div>

          {/* Change File Button */}
          <div className="text-center">
            <button
              onClick={() => {
                setSelectedFile(null);
                setPreviewUrl(null);
                setError('');
                if (fileInputRef.current) {
                  fileInputRef.current.value = '';
                }
              }}
              className="text-amber-600 hover:text-amber-700 font-medium"
            >
              Choose Different Image
            </button>
          </div>
        </div>
      )}

      {/* Host Modal */}
      {showHostModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
            <div className="text-center">
              <div className="text-6xl mb-4">🎨</div>
              <h3 className="text-2xl font-bold text-amber-800 mb-4">
                Share Your Artwork?
              </h3>
              <p className="text-amber-700 mb-6">
                Would you like to share your artwork with other young artists who have completed this activity? 
                They can see your creation and learn from your creativity!
              </p>
              
              <div className="flex gap-4 justify-center">
                <button
                  onClick={() => handleHostDecision(false)}
                  className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold py-3 px-6 rounded-full transition-colors"
                >
                  Keep Private
                </button>
                <button
                  onClick={() => handleHostDecision(true)}
                  className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-semibold py-3 px-6 rounded-full transition-colors"
                >
                  Share Publicly
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ArtworkUploadForm;
